/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import React from 'react';
import { useParams } from 'react-router-dom';
import get from 'lodash/fp/get';

import { useArticlesByIds, useMedia } from '@api/hooks';
import {
  TextField,
  ArticleSelect,
  ContentModuleModal,
  InfoItem,
  AddSelectEntity,
  SortableArrayField,
} from '@molecules';
import { postBy, formatArticleError } from '@utils';
import { getImage } from '@utils/media';

import Form from '../Form';

import { FORM_FIELD, ARTICLES_MAX } from './constants';
import Item from './Item';
import s from './styles.module.less';

const ArticleItem = ({ article }) => {
  const { media } = useMedia(getImage(article, 'featureImage'));

  if (article instanceof Error) {
    const error = article;
    return <InfoItem>{formatArticleError(error)}</InfoItem>;
  }

  return (
    <div className={s.articlesItem}>
      <InfoItem image={media.src} description={article.title ? postBy(article) : ''}>
        {article.title || 'Loading article...'}
      </InfoItem>
    </div>
  );
};

const NewsArticleRow = ({ visible, onSubmit, previewImg, onDelete, hide, initialValues: data, errors }) => {
  const [form] = Form.useForm(FORM_FIELD);
  const { locale } = useParams();

  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);

  const articles = useArticlesByIds(initialValues[FORM_FIELD.ARTICLES.name].map(get('id')), locale, {
    passErrors: true,
  });
  React.useEffect(() => {
    if (visible && articles.status === 'success' && articles.data) {
      form.setFieldsValue({ [FORM_FIELD.ARTICLES.name]: articles.data });
    }
  }, [articles, articles.data, articles.status, form, visible]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleFinish = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleAddArticle = async (article) => {
    form.setFieldsValue({ [FORM_FIELD.ARTICLES.name]: [...form.getFieldValue(FORM_FIELD.ARTICLES.name), article] });
  };

  return (
    <ContentModuleModal
      visible={visible}
      title="News article row"
      subtitle={`Choose up to ${ARTICLES_MAX} articles to appear in a row in the email`}
      okText={onDelete ? 'SAVE CHANGES' : 'ADD NEWS ARTICLE'}
      onDelete={onDelete}
      onCancel={handleClose}
      onSubmit={form.submit}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form form={form} layout="vertical" initialValues={initialValues} onSerializedFinish={handleFinish}>
        <AddSelectEntity label="Title:" addText="ADD ARTICLE" onAdd={handleAddArticle} select={ArticleSelect} />
        <div className={s.articlesList}>
          <Form.WithFieldValue name={FORM_FIELD.ARTICLES.name}>
            {() => (
              <SortableArrayField {...FORM_FIELD.ARTICLES}>
                {(value) => <ArticleItem article={value} />}
              </SortableArrayField>
            )}
          </Form.WithFieldValue>
        </div>
        <TextField {...FORM_FIELD.TITLE} />
      </Form>
    </ContentModuleModal>
  );
};

NewsArticleRow.Item = Item;

export default NewsArticleRow;
